import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Award, ThumbsUp, Heart, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { companyInfo, values, milestones, images } from "../data/data";

const iconMap = { ShieldCheck, Award, ThumbsUp, Heart };

// Page Header
const PageHeader = () => (
  <section className="bg-pc-primary pt-32 pb-20 texture-grain">
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-[2px] bg-pc-accent" />
        <span className="text-pc-accent font-heading text-sm font-semibold uppercase tracking-[0.15em]">
          About Us
        </span>
      </div>
      <h1 className="font-heading text-3xl md:text-4xl lg:text-[52px] font-extrabold text-white leading-[1.1] tracking-tight mb-5">
        Three Decades of
        <br />
        Honest Construction
      </h1>
      <p className="font-body text-white/50 text-lg max-w-2xl leading-relaxed">
        Since 1995, we have been building structures and relationships across Gujarat
        with a simple principle — do good work, and clients will keep coming back.
      </p>
    </motion.div>
  </section>
);

// Company Story
const CompanyStory = () => (
  <section className="bg-white py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="accent-bar mb-6" />
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-pc-primary tracking-tight mb-6">
            Our Story
          </h2>
          <div className="space-y-5 font-body text-pc-muted text-[15px] leading-[1.75]">
            <p>
              Established in 1995, Prayosha Construction is a trusted civil construction company based in Anand, Gujarat, delivering reliable residential and commercial building solutions for over 25 years. From general civil contracting to complete design-and-build projects, we specialize in renovation, repairs, additions and alterations, extensions, and new construction work with a strong focus on quality and precision.
            </p>
            <p>
              Our project management approach is built on detailed planning, transparent communication, and strict quality supervision. We work through rate contracts, lump-sum contracts, labor contracts, and area-based contracts — always aligning our execution with client objectives, timelines, and budgets. Safety, competitive pricing, and timely delivery remain at the core of every project we undertake.
            </p>
            <p>
              Led by experienced professionals in civil engineering and construction management, Prayosha Construction is committed to delivering beyond expectations. Our mission is simple: build with integrity, execute with excellence, and ensure complete client satisfaction in every project we complete.
            </p>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
            <img
              src={images.about}
              alt="Prayosha Construction - Building in progress"
              className="w-full h-full object-cover"
            />
          </div>
          {/* Accent frame */}
          <div className="absolute -bottom-4 -left-4 w-32 h-32 border-l-[3px] border-b-[3px] border-pc-accent rounded-bl-2xl hidden md:block" />
          {/* Experience badge */}
          <div className="absolute -bottom-6 right-8 bg-pc-primary text-white rounded-xl px-7 py-5 shadow-2xl hidden md:block">
            <p className="font-heading text-3xl font-extrabold">30+</p>
            <p className="font-body text-xs text-white/50 uppercase tracking-wider mt-1">Years Experience</p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Values
const ValuesSection = () => (
  <section className="bg-pc-light py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
        className="text-center max-w-xl mx-auto mb-16"
      >
        <div className="accent-bar mx-auto mb-5" />
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-pc-primary tracking-tight mb-4">
          What Guides Our Work
        </h2>
        <p className="font-body text-pc-muted text-base leading-relaxed">
          These aren't just words on a wall. They are principles we follow on
          every project, every day.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {values.map((value, index) => {
          const Icon = iconMap[value.icon];
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                className="bg-white border-pc-border/60 hover:shadow-xl card-accent-hover transition-shadow duration-300 rounded-xl h-full"
              >
                <CardContent className="p-7">
                  <div className="w-11 h-11 rounded-lg bg-pc-accent/8 flex items-center justify-center mb-5">
                    {Icon && <Icon className="w-5 h-5 text-pc-accent" />}
                  </div>
                  <h3 className="font-heading text-base font-bold text-pc-primary mb-2">
                    {value.title}
                  </h3>
                  <p className="font-body text-sm text-pc-muted leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

// Timeline
const Timeline = () => (
  <section className="bg-white py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl mb-16">
        <div className="accent-bar mb-5" />
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-pc-primary tracking-tight mb-4">
          Key Milestones
        </h2>
      </div>

      <div className="relative">
        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-pc-border md:-translate-x-px" />

        <div className="space-y-12">
          {milestones.map((milestone, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-0 ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
            >
              <div className={`md:w-1/2 pl-12 md:pl-0 ${index % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16"
                }`}>
                <span className="font-heading text-pc-accent font-bold text-lg">
                  {milestone.year}
                </span>
                <h3 className="font-heading text-lg font-bold text-pc-primary mt-1 mb-2">
                  {milestone.title}
                </h3>
                <p className="font-body text-sm text-pc-muted leading-relaxed">
                  {milestone.link ? (
                    <>
                      {milestone.description.split(milestone.link.text)[0]}
                      <a
                        href={milestone.link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-pc-accent hover:underline font-medium"
                      >
                        {milestone.link.text}
                      </a>
                      {milestone.description.split(milestone.link.text)[1]}
                    </>
                  ) : (
                    milestone.description
                  )}
                </p>
              </div>
              <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-pc-accent rounded-full -translate-x-1/2 mt-1 md:mt-0 ring-4 ring-white" />
              <div className="hidden md:block md:w-1/2" />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// CTA
const CtaSection = () => (
  <section className="bg-pc-primary py-20 texture-grain">
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
    >
      <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white tracking-tight mb-4">
        Want to Work With Us?
      </h2>
      <p className="font-body text-white/50 text-base max-w-lg mx-auto mb-8">
        Whether it's a new build, renovation, or repair — we'd be happy to
        discuss your requirements.
      </p>
      <Link to="/contact">
        <Button className="bg-pc-accent hover:bg-pc-accent/90 text-white text-sm font-heading font-semibold px-8 h-12 rounded-lg transition-colors duration-200">
          Get in Touch
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </motion.div>
  </section>
);

export default function About() {
  return (
    <main>
      <PageHeader />
      <CompanyStory />
      <ValuesSection />
      <Timeline />
      <CtaSection />
    </main>
  );
}
